import { FC } from 'react'
import HotelOrdersTab from './HotelOrdersTab'
import { SummaryTabProps } from '../types/tabTypes'
import { getReservationStatusLabel } from '../types'
import { i18n } from '../../../../translate/i18n'

const SummaryTab: FC<SummaryTabProps> = ({
  formData,
  selectedRoom,
  selectedCustomer,
  reservationSummary,
  hotelOrders,
  hotelOrdersTotal,
  loadingOrders,
  calculateDailyRate,
  onNavigateToTab,
}) => {
  const dailyInfo = calculateDailyRate()

  return (
    <div className='row'>
      <div className='col-12'>
        {/* Informações da Hospedagem Principal */}
        <div className='card card-custom mb-6'>
          <div className='card-header bg-light-primary'>
            <h3 className='card-title text-primary'>
              <i className='fas fa-bed me-2'></i>
              {i18n.t('hospitality_room_reservation_tabs.summary.tab_title')}
            </h3>
          </div>
          <div className='card-body'>
            <div className='row'>
              <div className='col-md-3'>
                <div className='text-center p-3 bg-light rounded'>
                  <i className='fas fa-calendar-day text-primary fs-2x mb-2'></i>
                  <h4 className='text-primary'>{dailyInfo.days}</h4>
                  <p className='text-muted mb-0'>
                    {i18n.t('hospitality_room_reservation_tabs.summary.label_total')}
                  </p>
                </div>
              </div>
              <div className='col-md-3'>
                <div className='text-center p-3 bg-light rounded'>
                  <i className='fas fa-money-bill text-success fs-2x mb-2'></i>
                  <h4 className='text-success'>R$ {dailyInfo.dailyRate.toFixed(2)}</h4>
                  <p className='text-muted mb-0'>{i18n.t('hospitality_room_reservation_tabs.summary.label_price')}</p>
                </div>
              </div>
              <div className='col-md-3'>
                <div className='text-center p-3 bg-light-success rounded'>
                  <i className='fas fa-calculator text-success fs-2x mb-2'></i>
                  <h4 className='text-success fw-bold'>R$ {dailyInfo.totalDaily.toFixed(2)}</h4>
                  <p className='text-muted mb-0'>{i18n.t('hospitality_room_reservation_tabs.summary.label_total')}</p>
                </div>
              </div>
              <div className='col-md-3'>
                <div className='text-center p-3 bg-light-info rounded'>
                  <i className='fas fa-clock text-info fs-2x mb-2'></i>
                  <div className='text-info fw-bold'>
                    <small className='d-block'>{i18n.t('hospitality_room_reservation_tabs.summary.label_checkin')}</small>
                    {dailyInfo.checkInDate.toLocaleDateString('pt-BR')}
                  </div>
                  <div className='text-info fw-bold mt-1'>
                    <small className='d-block'>
                      {formData.checkOut ? i18n.t('hospitality_room_reservation_tabs.summary.label_checkout') : i18n.t('hospitality_room_reservation_tabs.summary.label_pending')}
                    </small>
                    {dailyInfo.checkOutDate.toLocaleDateString('pt-BR')}
                  </div>
                </div>
              </div>
            </div>

            {selectedRoom && (
              <div className='row mt-4'>
                <div className='col-12'>
                  <div className='alert alert-info d-flex align-items-center'>
                    <i className='fas fa-info-circle me-3 fs-3'></i>
                    <div>
                      <strong>{i18n.t('hospitality_room_reservation_tabs.summary.label_room')}</strong> {selectedRoom?.roomNumber} - {selectedRoom?.type}
                      <br />
                      <small className='text-muted'>
                        {i18n.t('hospitality_room_reservation_tabs.summary.label_capacity')}: {selectedRoom.capacity} |
                        {i18n.t('hospitality_room_reservation_tabs.summary.label_status')}: {getReservationStatusLabel(formData.status)} |
                        {!formData.checkOut && i18n.t('hospitality_room_reservation_tabs.summary.label_status_in_progress')}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Resumo das Reservas Adicionais */}
        {reservationSummary && reservationSummary.totalReservations > 0 ? (
          <div className='card card-custom'>
            <div className='card-header'>
              <h3 className='card-title'>
                <i className='fas fa-chart-pie me-2'></i>
                {i18n.t('hospitality_room_reservation_tabs.summary.label_summary')}
              </h3>
              <div className='card-toolbar'>
                <span className='badge badge-light-primary fs-7'>
                  {i18n.t('hospitality_room_reservation_tabs.summary.label_details')}
                </span>
              </div>
            </div>
            <div className='card-body'>
              {/* Métricas Principais */}
              <div className='row mb-8'>
                <div className='col-md-3'>
                  <div className='text-center p-4 bg-light-primary rounded'>
                    <i className='fas fa-calendar-check text-primary fs-2x mb-2'></i>
                    <h3 className='text-primary'>{reservationSummary.totalReservations}</h3>
                    <p className='text-muted mb-0'>{i18n.t('hospitality_room_reservation_tabs.summary.label_total')}</p>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='text-center p-4 bg-light-success rounded'>
                    <i className='fas fa-money-bill-wave text-success fs-2x mb-2'></i>
                    <h3 className='text-success'>
                      {reservationSummary.currency} {reservationSummary.totalAmount.toFixed(2)}
                    </h3>
                    <p className='text-muted mb-0'>{i18n.t('hospitality_room_reservation_tabs.summary.label_total_paid')}</p>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='text-center p-4 bg-light-info rounded'>
                    <i className='fas fa-users text-info fs-2x mb-2'></i>
                    <h3 className='text-info'>{reservationSummary.totalParticipants}</h3>
                    <p className='text-muted mb-0'>{i18n.t('hospitality_room_reservation_tabs.summary.label_people')}</p>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='text-center p-4 bg-light-warning rounded'>
                    <i className='fas fa-percentage text-warning fs-2x mb-2'></i>
                    <h3 className='text-warning'>
                      {reservationSummary.totalReservations > 0 ?
                        ((reservationSummary.byStatus['confirmed']?.count || 0) / reservationSummary.totalReservations * 100).toFixed(1)
                        : 0}%
                    </h3>
                    <p className='text-muted mb-0'>{i18n.t('hospitality_room_reservation_tabs.summary.label_status_confirmed')}</p>
                  </div>
                </div>
              </div>

              {/* Breakdown por Categoria */}
              <div className='row mb-6'>
                <div className='col-md-3'>
                  <div 
                    className='card card-flush h-md-100 border' 
                    style={{ cursor: 'pointer' }}
                    onClick={() => onNavigateToTab?.('activities')}
                  >
                    <div className='card-header bg-light-success'>
                      <h3 className='card-title text-success'>
                        <i className='fas fa-running me-2'></i>
                        {i18n.t('hospitality_room_reservation_tabs.summary.label_activities')}
                      </h3>
                    </div>
                    <div className='card-body'>
                      <div className='d-flex align-items-center mb-3'>
                        <span className='fs-2hx fw-bold text-success me-2'>
                          {reservationSummary.byType.activities.count}
                        </span>
                        <span className='fs-6 fw-semibold text-gray-400'>{i18n.t('hospitality_room_reservation_tabs.summary.label_total')}</span>
                      </div>
                      <div className='fs-5 fw-bold text-success mb-2'>
                        R$ {reservationSummary.byType.activities.total.toFixed(2)}
                      </div>
                      {reservationSummary.byType.activities.count > 0 && (
                        <div className='fs-7 text-muted'>
                          {i18n.t('hospitality_room_reservation_tabs.summary.label_total_with_tax', { value: (reservationSummary.byType.activities.total / reservationSummary.byType.activities.count).toFixed(2) })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div 
                    className='card card-flush h-md-100 border'
                    style={{ cursor: 'pointer' }}
                    onClick={() => onNavigateToTab?.('tours')}
                  >
                    <div className='card-header bg-light-info'>
                      <h3 className='card-title text-info'>
                        <i className='fas fa-map-marked-alt me-2'></i>
                        {i18n.t('hospitality_room_reservation_tabs.summary.label_tours')}
                      </h3>
                    </div>
                    <div className='card-body'>
                      <div className='d-flex align-items-center mb-3'>
                        <span className='fs-2hx fw-bold text-info me-2'>
                          {reservationSummary.byType.tours.count}
                        </span>
                        <span className='fs-6 fw-semibold text-gray-400'>{i18n.t('hospitality_room_reservation_tabs.summary.label_total')}</span>
                      </div>
                      <div className='fs-5 fw-bold text-info mb-2'>
                        R$ {reservationSummary.byType.tours.total.toFixed(2)}
                      </div>
                      {reservationSummary.byType.tours.count > 0 && (
                        <div className='fs-7 text-muted'>
                          {i18n.t('hospitality_room_reservation_tabs.summary.label_total_with_tax', { value: (reservationSummary.byType.tours.total / reservationSummary.byType.tours.count).toFixed(2) })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div 
                    className='card card-flush h-md-100 border'
                    style={{ cursor: 'pointer' }}
                    onClick={() => onNavigateToTab?.('experiences')}
                  >
                    <div className='card-header bg-light-warning'>
                      <h3 className='card-title text-warning'>
                        <i className='fas fa-star me-2'></i>
                        {i18n.t('hospitality_room_reservation_tabs.summary.label_experiences')}
                      </h3>
                    </div>
                    <div className='card-body'>
                      <div className='d-flex align-items-center mb-3'>
                        <span className='fs-2hx fw-bold text-warning me-2'>
                          {reservationSummary.byType.experiences.count}
                        </span>
                        <span className='fs-6 fw-semibold text-gray-400'>{i18n.t('hospitality_room_reservation_tabs.summary.label_total')}</span>
                      </div>
                      <div className='fs-5 fw-bold text-warning mb-2'>
                        R$ {reservationSummary.byType.experiences.total.toFixed(2)}
                      </div>
                      {reservationSummary.byType.experiences.count > 0 && (
                        <div className='fs-7 text-muted'>
                          {i18n.t('hospitality_room_reservation_tabs.summary.label_total_with_tax', { value: (reservationSummary.byType.experiences.total / reservationSummary.byType.experiences.count).toFixed(2) })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div 
                    className='card card-flush h-md-100 border'
                    style={{ cursor: 'pointer' }}
                    onClick={() => onNavigateToTab?.('services')}
                  >
                    <div className='card-header bg-light-primary'>
                      <h3 className='card-title text-primary'>
                        <i className='fas fa-concierge-bell me-2'></i>
                        {i18n.t('hospitality_room_reservation_tabs.summary.label_services')}
                      </h3>
                    </div>
                    <div className='card-body'>
                      <div className='d-flex align-items-center mb-3'>
                        <span className='fs-2hx fw-bold text-primary me-2'>
                          {reservationSummary.byType.services.count}
                        </span>
                        <span className='fs-6 fw-semibold text-gray-400'>{i18n.t('hospitality_room_reservation_tabs.summary.label_total')}</span>
                      </div>
                      <div className='fs-5 fw-bold text-primary mb-2'>
                        R$ {reservationSummary.byType.services.total.toFixed(2)}
                      </div>
                      {reservationSummary.byType.services.count > 0 && (
                        <div className='fs-7 text-muted'>
                          {i18n.t('hospitality_room_reservation_tabs.summary.label_total_with_tax', { value: (reservationSummary.byType.services.total / reservationSummary.byType.services.count).toFixed(2) })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Status das Reservas */}
              <div className='row'>
                <div className='col-12'>
                  <div className='card card-flush'>
                    <div className='card-header'>
                      <h3 className='card-title'>
                        <i className='fas fa-list-check me-2'></i>
                        {i18n.t('hospitality_room_reservation_tabs.summary.label_status')}
                      </h3>
                    </div>
                    <div className='card-body'>
                      <div className='row'>
                        {Object.entries(reservationSummary.byStatus).map(([status, data]) => (
                          <div key={status} className='col-md-3 mb-3'>
                            <div className={`text-center p-3 rounded border border-${status === 'confirmed' ? 'success' :
                              status === 'cancelled' ? 'danger' :
                                status === 'pending' ? 'warning' : 'secondary'
                              }`}>
                              <h4 className={`text-${status === 'confirmed' ? 'success' :
                                status === 'cancelled' ? 'danger' :
                                  status === 'pending' ? 'warning' : 'secondary'
                                }`}>
                                {data.count}
                              </h4>
                              <p className='text-muted mb-1 text-capitalize'>{i18n.t(`hospitality_room_reservation_tabs.summary.label_status_${status}`)}</p>
                              <small className='text-muted'>
                                R$ {data.total.toFixed(2)}
                              </small>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className='text-center py-10'>
            <i className='fas fa-chart-pie text-muted fs-3x mb-4'></i>
            <h3 className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.summary.label_no_data')}</h3>
            <p className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.summary.label_pending')}</p>
          </div>
        )}

        {/* Pedidos das Lojas do Hotel */}
        <div className='mt-8'>
          {/* Ensure HotelOrdersTab is imported at the top of the file */}
          <HotelOrdersTab
            orders={hotelOrders}
            totalSpent={hotelOrdersTotal.totalSpent}
            totalPay={hotelOrdersTotal.totalPay}
            loading={loadingOrders}
            showList={false}
          />
        </div>
      </div>
    </div>
  )
}

export default SummaryTab